import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import moment from "moment";
import { CategoryChartData } from "@/models/category-chart-data";
import { TimelineChartData } from "@/models/timeline-chart-data";

@Module({ name: "RecognitionAnalyticsStore", namespaced: true })
class RecognitionAnalyticsStore extends VuexModule {
  private dateRange: { [key: string]: string } = {
    startDate: moment()
      .subtract(1, "weeks")
      .format()
      .toString()
      .substring(0, 10),
    endDate: moment()
      .format()
      .toString()
      .substring(0, 10)
  };
  private selectedSite = "";
  private recogByHeaderFieldData: CategoryChartData[] = [];
  private recogBySupplierData: CategoryChartData[] = [];
  private supplierInvoiceVolumeData: CategoryChartData[] = [];
  private countryInvoiceVolumeData: CategoryChartData[] = [];
  private recogHistoricalData: {
    [key: string]: TimelineChartData[];
  } = {};
  private dailyRecogByHeaderFieldData: {
    [key: string]: CategoryChartData[];
  } = {};
  private averageOverallRecogRate = "0%";
  private invoicesAnalyzed = "0";
  private numberOfSuppliers = 0;
  private searchButtonClicked = 0;
  private includeVsInRecogRates = true;

  @Mutation
  private setDateRange(dateRange: { [key: string]: string }) {
    this.dateRange = dateRange;
  }

  @Mutation
  private setRecogByHeaderFieldData(data: CategoryChartData[]) {
    this.recogByHeaderFieldData = data;
  }

  @Mutation
  private setRecogBySupplierData(data: CategoryChartData[]) {
    this.recogBySupplierData = data;
  }

  @Mutation
  private setSupplierInvoiceVolumeData(data: CategoryChartData[]) {
    this.supplierInvoiceVolumeData = data;
  }

  @Mutation
  private setCountryInvoiceVolumeData(data: CategoryChartData[]) {
    this.countryInvoiceVolumeData = data;
  }

  @Mutation
  private setRecogHistoricalData(data: { [key: string]: TimelineChartData[] }) {
    this.recogHistoricalData = data;
  }

  @Mutation
  private setDailyRecogByHeaderFieldData(data: {
    [key: string]: CategoryChartData[];
  }) {
    this.dailyRecogByHeaderFieldData = data;
  }

  @Mutation
  private setAverageOverallRecogRate(rate: string) {
    this.averageOverallRecogRate = rate;
  }

  @Mutation
  private setInvoicesAnalyzed(numOfInvoices: string) {
    this.invoicesAnalyzed = numOfInvoices;
  }

  @Mutation
  private setNumberOfSuppliers(numOfSuppliers: number) {
    this.numberOfSuppliers = numOfSuppliers;
  }

  @Mutation
  private setSearchButtonClicked() {
    this.searchButtonClicked += 1;
  }

  @Mutation
  private setSelectedSite(site: string) {
    this.selectedSite = site;
  }

  @Mutation
  private setIncludeVsInRecogRates(includeVsInRecogRates: boolean) {
    this.includeVsInRecogRates = includeVsInRecogRates;
  }

  @Action({ rawError: true })
  public updateDateRange(dateRange: { [key: string]: string }) {
    this.context.commit("setDateRange", dateRange);
  }

  @Action({ rawError: true })
  public updateRecogByHeaderFieldData(data: CategoryChartData[]) {
    this.context.commit("setRecogByHeaderFieldData", data);
  }

  @Action({ rawError: true })
  public updateRecogBySupplierData(data: CategoryChartData[]) {
    this.context.commit("setRecogBySupplierData", data);
  }

  @Action({ rawError: true })
  public updateSupplierInvoiceVolumeData(data: CategoryChartData[]) {
    this.context.commit("setSupplierInvoiceVolumeData", data);
  }

  @Action({ rawError: true })
  public updateCountryInvoiceVolumeData(data: CategoryChartData[]) {
    this.context.commit("setCountryInvoiceVolumeData", data);
  }

  @Action({ rawError: true })
  public updateRecogHistoricalData(data: {
    [key: string]: TimelineChartData[];
  }) {
    this.context.commit("setRecogHistoricalData", data);
  }

  @Action({ rawError: true })
  public updateDailyRecogByHeaderFieldData(data: {
    [key: string]: CategoryChartData[];
  }) {
    this.context.commit("setDailyRecogByHeaderFieldData", data);
  }

  @Action({ rawError: true })
  public updateAverageOverallRecogRate(rate: string) {
    this.context.commit("setAverageOverallRecogRate", rate);
  }

  @Action({ rawError: true })
  public updateInvoicesAnalyzed(numOfInvoices: number) {
    this.context.commit("setInvoicesAnalyzed", numOfInvoices);
  }

  @Action({ rawError: true })
  public updateNumberOfSuppliers(numOfSuppliers: number) {
    this.context.commit("setNumberOfSuppliers", numOfSuppliers);
  }

  @Action({ rawError: true })
  public clickRecogSearchButton() {
    this.context.commit("setSearchButtonClicked");
  }

  @Action({ rawError: true })
  public updateSelectedSite(site: string) {
    this.context.commit("setSelectedSite", site);
  }

  @Action({ rawError: true })
  public updateIncludeVsInRecogRates(includeVsInRecogRates: boolean) {
    this.context.commit("setIncludeVsInRecogRates", includeVsInRecogRates);
  }

  public get getDateRange(): { [key: string]: string } {
    return this.dateRange;
  }

  public get getRecogByHeaderFieldData(): CategoryChartData[] {
    return this.recogByHeaderFieldData;
  }

  public get getRecogBySupplierData(): CategoryChartData[] {
    return this.recogBySupplierData;
  }

  public get getSupplierInvoiceVolumeData(): CategoryChartData[] {
    return this.supplierInvoiceVolumeData;
  }

  public get getCountryInvoiceVolumeData(): CategoryChartData[] {
    return this.countryInvoiceVolumeData;
  }

  public get getRecogHistoricalData(): {
    [key: string]: TimelineChartData[];
  } {
    return this.recogHistoricalData;
  }

  public get getDailyRecogByHeaderFieldData(): {
    [key: string]: CategoryChartData[];
  } {
    return this.dailyRecogByHeaderFieldData;
  }

  public get getAverageOverallRecogRate(): string {
    return this.averageOverallRecogRate;
  }

  public get getInvoicesAnalyzed(): string {
    return this.invoicesAnalyzed;
  }

  public get getNumberOfSuppliers(): number {
    return this.numberOfSuppliers;
  }

  public get getSearchButtonClicked(): number {
    return this.searchButtonClicked;
  }

  public get getSelectedSite(): string {
    return this.selectedSite;
  }

  public get getIncludeVsInRecogRates(): boolean {
    return this.includeVsInRecogRates;
  }
}

export default RecognitionAnalyticsStore;
